import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import {
  HelpCircle,
  Map,
  Settings2,
  Loader2,
  BarChart3,
  ExternalLink,
  KeyRound,
} from "lucide-react"
import toast from "react-hot-toast"

import { NavMain, type Item } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { OrganizationSwitcher } from "./organization-switcher"
import BillingInfoCard from "./features/billing/BillingInfoCard"
import { useHelpCenterStore } from "@/store/helpCenterStore"
import { useUserHasHelpCenterEnabled, useIsAdmin } from "@/store/userStore"
import { usePenpal } from "@/hooks/usePenpal"
import { generateBrowserLoginToken } from "@/services/api"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { helpCenters, isLoading, fetchHelpCenters } = useHelpCenterStore();
  const { openExternalUrl, isInIframe: inIframe } = usePenpal();
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const helpCenterEnabled = useUserHasHelpCenterEnabled();
  const isAdmin = useIsAdmin();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Fetch help centers on mount
  useEffect(() => {
    if(helpCenterEnabled) {
      fetchHelpCenters();
    }
  }, [fetchHelpCenters, helpCenterEnabled]);


  const handleOpenInBrowser = async () => {
    setIsGeneratingToken(true);
    try {
      const response = await generateBrowserLoginToken();
      if (response.ok && response.url) {
        toast.success("Opening in browser...", {
          position: "top-center",
          duration: 3000,
        });
        openExternalUrl(response.url);
      } else {
        throw new Error(response.error || "Failed to generate login token");
      }
    } catch (error) {
      console.error("Error generating browser login token:", error);
      toast.error(
        "Failed to open in browser. Please try again or contact support.",
        {
          position: "top-center",
          duration: 5000,
        }
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Build navigation items with help centers
  const navigationTop = useMemo(() => {
    let helpCenterChildren: Item[] = [];

    // Add loading indicator when fetching
    if (isLoading) {
      helpCenterChildren.push({
        title: "Loading...",
        tooltip: "Loading help centers",
        icon: Loader2,
      } as Item);
    } else if (helpCenters.length > 0) {
      // Add help centers as navigation items
      helpCenters.forEach((helpCenter) => {
        helpCenterChildren.push({
          title: helpCenter.title,
          url: `/help-centers/${helpCenter.id}/structure`,
          tooltip: helpCenter.description || helpCenter.title,
        });
      });
    }
    if(!helpCenterEnabled) {
      helpCenterChildren = [];
    }
    return [
      // {
      //   title: "Collections",
      //   url: "collections",
      //   icon: BookOpen,
      //   description: "Manage your knowledge base",
      // },
      isAdmin ? {
        title: "License Manager",
        url: "/license-manager",
        icon: KeyRound,
        tooltip: "Assign and manage licenses",
        highlight: true,
      } : null,
      {
        title: "Guides",
        url: "/guides",
        tooltip: "List of all guides",
        icon: Map,
      },
      helpCenterEnabled ? {
        title: "Help Centers",
        icon: HelpCircle,
        url: "/help-centers",
        children: helpCenterChildren,
        badge: "New",
      } : null,
      {
        title: "Guides Branding",
        url: "/settings/guides-appearance",
        icon: Settings2,
        tooltip: "Customize branding for all guides",
      },
      helpCenterEnabled ? {
        title: "Reports",
        url: "/reports",
        icon: BarChart3,
        tooltip: "View help center feedback",
      } : null,
      // {
      //   title: "Articles",
      //   url: "articles",
      //   icon: FileText,
      //   description: "All articles",
      // },
    ].filter(Boolean) as Item[];
  }, [helpCenters, isLoading, isAdmin]);

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <OrganizationSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navigationTop} className="mt-2"/>
        {/* <NavProjects projects={data.projects} /> */}
        {/* <NavSecondary 
          items={navigationBottom} 
          className="mt-auto"
        /> */}
        <div className="mx-2 mt-auto mb-4">
          <BillingInfoCard hideHelpCenters={inIframe} />
        </div>
      </SidebarContent>
      {inIframe && helpCenterEnabled && (
        <SidebarFooter className="border-t border-sidebar-border pt-2">
          <div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full h-8 font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground border-primary shadow-none border-2 justify-center`}
                    onClick={handleOpenInBrowser}
                    disabled={isGeneratingToken}
                  >
                    {isGeneratingToken ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {!isCollapsed && <span>Opening...</span>}
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        {!isCollapsed && <span className="ml-2">Open In Browser</span>}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Open Folge Cloud UI in your browser and continue working there.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </SidebarFooter>
      )}
      <SidebarRail />
    </Sidebar>
  )
}
